/* Osmo home page interactions — vanilla JS port of the React stripes.
   Handles: welcome flip cards, play stripe skill cycling, works lottie,
   featured carousel, video popups, country selector. */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // Run `fn` once a global (e.g. the `lottie` library) is defined. The library
  // loads from a separate <script>, and anything that reorders or delays script
  // execution — Cloudflare Rocket Loader, a consent manager, a cold cache — can
  // run this file before that global exists. Rather than give up, poll briefly
  // (up to ~10s) so the animation still initializes whenever the library lands.
  function whenGlobal(name, fn) {
    if (typeof window[name] !== 'undefined') { fn(); return; }
    var waited = 0;
    var timer = setInterval(function () {
      if (typeof window[name] !== 'undefined') { clearInterval(timer); fn(); }
      else if ((waited += 50) >= 10000) { clearInterval(timer); }
    }, 50);
  }

  // Map `data-video-name` -> YouTube watch URL.
  // Source: extracted from playosmo.com popup-video-link-bundle metadata.
  var VIDEO_POPUPS = {
    'osmo-bursting-with-fun-2018': { youtube: 'XYwIZhlEa1A' },
    'osmo-schools':                { youtube: 'zEsJwwdq4r8' },
    'osmo-pizza-co':               { youtube: 'RGe67qYIZdQ' },
    'osmo-coding':                 { youtube: 'I9Qm18it47A' },
    'osmo-coding-jam':             { youtube: 'jn56Dm6Ozbg' },
    'osmo-coding-duo':             { youtube: 'k1F1cNu1pOI' },
    'osmo-monster':                { youtube: 'PBEQfnbiBoQ' },
    'osmo-hotwheels-mindracers':   { youtube: 'dDTMhP0wvdA' },
    'osmo-numbers':                { youtube: 'jL2Ew02gJKo' },
    'osmo-super-studio':           { youtube: 'zLeK67FpKYU' },
  };

  // ---------- Welcome stripe: flip cards on hover (desktop) / tap (touch) ----------
  function initWelcome(root) {
    var hasTouch = matchMedia('(hover: none)').matches;
    var items = root.querySelectorAll('.home-stripe2019-welcome-item');

    items.forEach(function (item) {
      var front = item.querySelector('.home-stripe2019-welcome-item-front');
      var back = item.querySelector('.home-stripe2019-welcome-item-back');
      if (!front || !back) return;

      function show(side) {
        if (side === 'back') {
          front.setAttribute('data-flipped', 'right');
          back.setAttribute('data-flipped', 'no');
        } else {
          front.setAttribute('data-flipped', 'no');
          back.setAttribute('data-flipped', 'right');
        }
      }

      if (hasTouch) {
        item.addEventListener('click', function (e) {
          var flipped = back.getAttribute('data-flipped') === 'no';
          if (!flipped) {
            e.preventDefault();
            show('back');
          }
        });
        document.addEventListener('click', function (e) {
          if (!item.contains(e.target)) show('front');
        });
      } else {
        item.addEventListener('mouseenter', function () { show('back'); });
        item.addEventListener('mouseleave', function () { show('front'); });
      }
    });
  }

  // ---------- Play stripe: cycle skill videos ----------
  function initPlay(root) {
    var holders = Array.prototype.slice.call(
      root.querySelectorAll('.home-stripe2019-play-video-holder')
    );
    var skillPanels = Array.prototype.slice.call(
      root.querySelectorAll('.home-stripe2019-play-skill')
    );
    if (!holders.length) return;

    var skills = holders.map(function (h) { return h.getAttribute('data-skill'); });
    var idx = 0;
    var advanceTimer = null;

    function selectByIndex(i) {
      idx = (i + skills.length) % skills.length;
      var current = skills[idx];

      holders.forEach(function (h) {
        var on = h.getAttribute('data-skill') === current;
        h.style.display = on ? '' : 'none';
        var v = h.querySelector('video');
        if (!v) return;
        if (on) {
          try { v.currentTime = 0; } catch (e) {}
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          try { v.pause(); } catch (e) {}
        }
      });

      skillPanels.forEach(function (p) {
        var on = p.getAttribute('data-skill') === current;
        p.setAttribute('data-selected', on ? 'true' : 'false');
      });
    }

    function advance() {
      clearTimeout(advanceTimer);
      selectByIndex(idx + 1);
      scheduleAdvance();
    }

    function scheduleAdvance() {
      clearTimeout(advanceTimer);
      var v = holders[idx].querySelector('video');
      var duration = v && isFinite(v.duration) && v.duration > 0 ? v.duration * 1000 : 8000;
      advanceTimer = setTimeout(advance, duration + 200);
    }

    // When current video naturally ends, advance
    holders.forEach(function (h) {
      var v = h.querySelector('video');
      if (v) {
        v.addEventListener('ended', function () {
          if (h.getAttribute('data-skill') === skills[idx]) advance();
        });
        v.addEventListener('loadedmetadata', function () {
          if (h.getAttribute('data-skill') === skills[idx]) scheduleAdvance();
        });
      }
    });

    // Click a skill pill to jump to that skill
    skillPanels.forEach(function (panel) {
      panel.addEventListener('click', function (e) {
        e.preventDefault();
        var target = panel.getAttribute('data-skill');
        var i = skills.indexOf(target);
        if (i >= 0) {
          selectByIndex(i);
          scheduleAdvance();
        }
      });
    });

    selectByIndex(0);
    scheduleAdvance();
  }

  // ---------- Works stripe: lottie how-it-works animation, cycling base/play/alive ----------
  function initWorks(root) {
    var target = root.querySelector('.js-home-stripe2019-works-animation-target');
    if (!target) return;

    // The `lottie` library may not be defined yet if scripts ran out of order;
    // wait for it instead of bailing, so the animation always starts.
    whenGlobal('lottie', function () {
      // Frame ranges mirror the React source:
      //   base:  [0,   5*30+10]
      //   play:  [5*30+10, 9*30]
      //   alive: [9*30, 12*30]
      var STEPS = [
        { name: 'base',  in: 0,       out: 5 * 30 + 10 },
        { name: 'play',  in: 5 * 30 + 10, out: 9 * 30 },
        { name: 'alive', in: 9 * 30,  out: 12 * 30 },
      ];

      var anim = lottie.loadAnimation({
        container: target,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        path: target.getAttribute('data-lottie-src'),
      });

      var stepEls = Array.prototype.slice.call(
        root.querySelectorAll('.js-home-stripe2019-works-step')
      );
      var step = 0;
      function playStep(i) {
        step = i % STEPS.length;
        var s = STEPS[step];
        stepEls.forEach(function (el) {
          el.setAttribute('data-show', el.getAttribute('data-step-id') === s.name ? 'true' : 'false');
        });
        anim.playSegments([s.in, s.out], true);
      }
      anim.addEventListener('DOMLoaded', function () { playStep(0); });
      anim.addEventListener('complete', function () {
        setTimeout(function () { playStep(step + 1); }, 300);
      });
    });
  }

  // ---------- Generic osmo-carousel: arrows + pips, cross-fade ----------
  function initCarousel(root) {
    var items = Array.prototype.slice.call(root.querySelectorAll(':scope > .osmo-carousel-item'));
    if (items.length < 2) return;
    var pips = Array.prototype.slice.call(
      root.querySelectorAll('.osmo-carousel-controls-pips .osmo-carousel-controls-pip')
    );
    var arrowLeft = root.querySelector('.osmo-carousel-arrow[data-direction="left"]');
    var arrowRight = root.querySelector('.osmo-carousel-arrow[data-direction="right"]');

    // Prep: remove data-first (which disables CSS animation), mark all as carousel-fade items
    items.forEach(function (it, k) {
      it.removeAttribute('data-first');
      it.classList.add('osmo-carousel-item-fade');
      it.setAttribute('data-active', k === 0 ? 'true' : 'false');
    });

    var idx = 0;
    function show(i) {
      idx = (i + items.length) % items.length;
      items.forEach(function (it, k) {
        it.setAttribute('data-active', k === idx ? 'true' : 'false');
      });
      pips.forEach(function (p, k) {
        p.setAttribute('data-selected', k === idx ? 'true' : 'false');
      });
    }

    if (arrowLeft) arrowLeft.addEventListener('click', function (e) { e.preventDefault(); show(idx - 1); });
    if (arrowRight) arrowRight.addEventListener('click', function (e) { e.preventDefault(); show(idx + 1); });
    pips.forEach(function (p, k) {
      p.addEventListener('click', function (e) { e.preventDefault(); show(k); });
    });
  }

  // ---------- Dialogs: video popups + country selector (a11y-dialog) ----------
  function initDialogs() {
    if (typeof window.A11yDialog === 'undefined') return;

    var videoEl = document.getElementById('osmo-home-video-dialog');
    var countryEl = document.getElementById('osmo-home-country-dialog');
    var videoSlot = videoEl && videoEl.querySelector('.js-osmo-dialog-video-slot');

    var videoDialog = videoEl ? new window.A11yDialog(videoEl) : null;
    var countryDialog = countryEl ? new window.A11yDialog(countryEl) : null;

    if (videoDialog && videoSlot) {
      videoDialog.on('hide', function () { videoSlot.innerHTML = ''; });
    }

    function openVideoForName(name) {
      if (!videoDialog || !videoSlot) return;
      var info = VIDEO_POPUPS[name];
      if (!info || !info.youtube) return;
      var src = 'https://www.youtube.com/embed/' + info.youtube + '?autoplay=1&rel=0';
      videoSlot.innerHTML =
        '<iframe src="' + src + '" title="Video" frameborder="0" ' +
        'allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" ' +
        'allowfullscreen></iframe>';
      videoDialog.show();
    }

    document.querySelectorAll('.js-popup-video[data-video-name], [data-video-name].js-popup-video').forEach(function (el) {
      el.addEventListener('click', function (e) {
        var name = el.getAttribute('data-video-name');
        if (!name || !VIDEO_POPUPS[name]) return;
        e.preventDefault();
        openVideoForName(name);
      });
    });

    if (countryDialog) {
      document.querySelectorAll('.shopping-countrychangericon').forEach(function (el) {
        el.addEventListener('click', function (e) {
          e.preventDefault();
          countryDialog.show();
        });
      });
    }
  }

  // ---------- Mobile menu: hamburger toggle, tap-to-close, expandable items ----------
  function initMobileMenu() {
    var toggle = document.querySelector('.js-mobilemenu-toggle');
    var drawer = document.querySelector('.js-mobilemenu');
    if (!toggle || !drawer) return;

    var openables = document.querySelectorAll('.js-mobilemenu-openable, .mobilemenu-openable-fadeout');
    var tapToClose = document.querySelector('.navbar-tap-to-close');

    function setState(expanded) {
      var state = expanded ? 'expanded' : 'collapsed';
      toggle.setAttribute('data-state', state);
      drawer.setAttribute('data-state', state);
      openables.forEach(function (el) { el.setAttribute('data-mobilemenu-open', expanded ? 'true' : 'false'); });
      if (tapToClose) tapToClose.style.display = expanded ? 'block' : 'none';
      document.body.style.overflow = expanded ? 'hidden' : '';
    }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      var isExpanded = toggle.getAttribute('data-state') === 'expanded';
      setState(!isExpanded);
    });

    if (tapToClose) {
      tapToClose.addEventListener('click', function () { setState(false); });
    }

    drawer.querySelectorAll('a.mobilemenu__item').forEach(function (link) {
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) === '#') {
        link.addEventListener('click', function () { setState(false); });
      }
    });

    drawer.querySelectorAll('.mobilemenu__item--expandable').forEach(function (item) {
      item.addEventListener('click', function (e) {
        e.preventDefault();
        var open = item.getAttribute('data-expanded') === 'true';
        item.setAttribute('data-expanded', open ? 'false' : 'true');
      });
    });

    // Close on viewport resize above tablet breakpoint (768px)
    window.addEventListener('resize', function () {
      if (window.innerWidth >= 768 && toggle.getAttribute('data-state') === 'expanded') {
        setState(false);
      }
    });
  }

  ready(function () {
    var welcome = document.querySelector('.home-stripe2019-welcome');
    if (welcome) initWelcome(welcome);

    var play = document.querySelector('.home-stripe2019-play');
    if (play) initPlay(play);

    var works = document.querySelector('.home-stripe2019-works');
    if (works) initWorks(works);

    document.querySelectorAll('.osmo-carousel').forEach(initCarousel);

    initDialogs();
    initMobileMenu();
  });
})();
